import {
  LOST_REASONS,
  REASON_BREAKAGE,
  normalizeLostReasonCode,
} from "@/constants";
import { formatDate, getDateByTimeZone } from "@/lib/date";
import {
  getCorpUserDisplayName,
  parseEtc4,
  renderApprovalDisplay,
} from "@/lib/approvalDisplay";

import BaseModel from "./base-model";
import { parseDeliveryStatus, renderStatus } from "./Delivery";

const LIST_STATUS_LABEL = {
  inProgress: "배송완료 전",
  completed: "배송완료",
  sentBack: "반송완료",
  lost: "분실완료",
  canceled: "취소",
};

const compactText = (value = "") => String(value).replace(/\s/g, "");

export default class LostDelivery extends BaseModel {
  getEtc4Obj() {
    if (this._etc4Obj) {
      return this._etc4Obj;
    }

    if (!this.deliveryEtc4 && !this.etc4 && !this.delivery?.etc4) {
      this._etc4Obj = {};
      return this._etc4Obj;
    }

    if (typeof this.deliveryEtc4 === "object") {
      this._etc4Obj = this.deliveryEtc4;
      return this._etc4Obj;
    }

    if (typeof this.etc4 === "object") {
      this._etc4Obj = this.etc4;
      return this._etc4Obj;
    }

    this._etc4Obj = parseEtc4(
      this.deliveryEtc4 || this.etc4 || this.delivery?.etc4,
    );

    return this._etc4Obj;
  }

  renderDateTime(value) {
    if (!value) {
      return "";
    }

    return formatDate(getDateByTimeZone(value), "yyyy-MM-dd HH:mm");
  }

  renderBookId() {
    return this.bookId || this.delivery?.bookId || "";
  }

  renderOrderIdFromCorp() {
    return this.orderIdFromCorp || this.delivery?.orderIdFromCorp || "";
  }

  renderOrderIdFromCorpSub() {
    return this.orderIdFromCorpSub || this.delivery?.orderIdFromCorpSub || "";
  }

  getRawDeliveryStatus() {
    return this.deliveryStatus ?? this.status ?? this.delivery?.status;
  }

  renderDeliveryStatus() {
    const status = this.getRawDeliveryStatus();

    if (status == null || status === "") {
      return "";
    }

    if (LIST_STATUS_LABEL[status]) {
      return LIST_STATUS_LABEL[status];
    }

    if (typeof status === "number" || /^\d+$/.test(String(status))) {
      return renderStatus(parseDeliveryStatus(status));
    }

    const labeled = renderStatus(status);

    return labeled === "접수" && status !== "DELIVERY_STATUS_RECEIPT"
      ? status
      : labeled;
  }

  getNormalizedLostReasonCode() {
    return normalizeLostReasonCode(
      this.lostReasonCode || this.getEtc4Obj().lostReasonCode || "",
    );
  }

  renderLostReason() {
    const code = this.getNormalizedLostReasonCode();
    const mapped = LOST_REASONS.find((item) => item.code === code);

    if (mapped) {
      return mapped.text;
    }

    return (
      this.getEtc4Obj().lostReason ||
      this.lostReason ||
      this.damagedReason ||
      this.reason ||
      code ||
      ""
    );
  }

  renderLostCompletedAt() {
    return this.renderDateTime(this.deliveryCompletedDate);
  }

  getReturnApprovedAt() {
    return this.returnApprovedAt || this.getEtc4Obj().returnApprovedAt || "";
  }

  getReturnApprovedByCorpUserId() {
    return (
      this.returnApprovedByCorpUserId ??
      this.getEtc4Obj().returnApprovedByCorpUserId ??
      ""
    );
  }

  getReturnApproverName() {
    return (
      this.returnApproverName ||
      this.returnApprovedByName ||
      getCorpUserDisplayName(this.returnApprovedByCorpUser) ||
      getCorpUserDisplayName(this.getEtc4Obj().returnApprovedByCorpUser) ||
      ""
    );
  }

  renderReturnApprovedAt() {
    return renderApprovalDisplay({
      approvedAt: this.getReturnApprovedAt(),
      corpUserId: this.getReturnApprovedByCorpUserId(),
      displayName: this.getReturnApproverName(),
      allowAuto: false,
      formatAt: (value) => this.renderDateTime(value),
    });
  }

  getDisposeConfirmedAt() {
    return this.disposeConfirmedAt || this.getEtc4Obj().disposeConfirmedAt || "";
  }

  getDisposeApprovedByCorpUserId() {
    return (
      this.disposeApprovedByCorpUserId ??
      this.getEtc4Obj().disposeApprovedByCorpUserId ??
      ""
    );
  }

  getDisposeApproverName() {
    return (
      this.disposeApproverName ||
      this.disposeApprovedByName ||
      getCorpUserDisplayName(this.disposeApprovedByCorpUser) ||
      getCorpUserDisplayName(this.getEtc4Obj().disposeApprovedByCorpUser) ||
      ""
    );
  }

  renderDisposeConfirmedAt() {
    return renderApprovalDisplay({
      approvedAt: this.getDisposeConfirmedAt(),
      corpUserId: this.getDisposeApprovedByCorpUserId(),
      displayName: this.getDisposeApproverName(),
      allowAuto: true,
      formatAt: (value) => this.renderDateTime(value),
    });
  }

  renderLatestCommentWriterName() {
    return (
      this.latestCommentWriterName || this.latestComment?.writerName || ""
    );
  }

  renderLatestCommentCreatedAt() {
    return this.renderDateTime(
      this.latestCommentCreatedAt || this.latestComment?.createdAt,
    );
  }

  renderCommentConfirmed() {
    const value =
      this.commentConfirmed ??
      this.latestCommentConfirmed ??
      this.latestComment?.isConfirmed;

    if (value == null) {
      return "";
    }

    return value ? "확인" : "미확인";
  }

  renderReceiptLabel() {
    const parts = [
      this.renderBookId(),
      this.renderOrderIdFromCorp(),
      this.renderOrderIdFromCorpSub(),
    ].filter(Boolean);

    return parts.join("/");
  }

  isDamageInTransit() {
    if (this.getNormalizedLostReasonCode() === REASON_BREAKAGE) {
      return true;
    }

    return compactText(this.renderLostReason()).includes("배송중파손");
  }

  isDisposalApproved() {
    return Boolean(this.disposeConfirmedAt);
  }

  isReturnApproved() {
    return Boolean(this.returnApprovedAt);
  }

  canApproveDisposal() {
    if (!this.isDamageInTransit() || this.isDisposalApproved() || this.isReturnApproved()) {
      return false;
    }

    return this.canDisposeApprove === true;
  }

  canWaitDisposal() {
    if (!this.isDamageInTransit() || this.isDisposalApproved() || this.isReturnApproved()) {
      return false;
    }

    return this.canDisposeWait === true;
  }

  canApproveReturn() {
    if (!this.isDamageInTransit() || this.isDisposalApproved() || this.isReturnApproved()) {
      return false;
    }

    return this.canReturnApprove === true;
  }
}
